import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as CirclePlus } from '../../svg/chevron-down.svg';
import { connect } from 'react-redux';
import ReactModal from 'react-modal';
import ExtraPackageDetails from '../ExtraPackages/ExtraPackageDetails';
import cn from 'classnames';
import { productCurrentProductIdSelector } from '../../redux/selectors/productSelector';
import {
    cartCartSelector,
    cartCurrencySelector,
    cartMainProduct,
} from '../../redux/selectors/cartSelector';
import getBillingUnitsCount from '../../resolvers/getBillingUnitsCount';
import getExtraPackageNote from '../../resolvers/getExtraPackageNote';
import isPeriodDifferentByProducts from '../../resolvers/isPeriodDifferentByProducts';
import getCostPerUnitNumber from '../../resolvers/getCostPerUnitNumber';
import { MINIMUM_ALLOWED_PRODUCT_PRICE } from '../../constants/constants';
import { pageLoading } from '../../redux/selectors/pagesSelectors';
import Skeleton from 'react-loading-skeleton';
import getExtraPackageNoteMainProduct from '../../resolvers/getExtraPackageNoteMainProduct';
import { formatNumberWithSeparator } from '../../lib/formatNumberWithSeparator';

function TableBody({
    product_id,
    product,
    currency,
    includeExtra,
    cart,
    mainProduct,
    pageLoading,
    checkboxExtraPg,
}) {
    const [modalOpened, setModalOpened] = useState(false);
    const [tariffCost, setTariffCost] = useState('-');
    const [costPerSite, setCostPerSite] = useState('-');
    const [extraSumPerSite, setExtraSumPerSite] = useState('-');
    const [savedExtraPerSite, setSavedExtraPerSite] = useState('-');
    const [extraVisible, setExtraVisible] = useState(false);
    const [descriptionVisible, setDescriptionVisible] = useState(true);
    const sign = <span dangerouslySetInnerHTML={{ __html: currency?.sign }}></span>;
    const [billingUnits, setBillingUnits] = useState(0);
    const [savedAmount, setSavedAmount] = useState(null);
    const [savedExtraAmount, setSavedExtraAmount] = useState(null);

    const getExtraCostPer = () => (includeExtra ? Number(product?.tariff?.cost * currency?.rate * product?.extra_package_rate).toFixed(2) : Number(0).toFixed(2));

    const getExtraSum = () => (includeExtra ? Number(getExtraCostPer() * product?.period?.interval).toFixed(2) : Number(0).toFixed(2));

    const sum = useMemo(() => {
        const extraSum = getExtraSum();

        if (!extraSum || isNaN(extraSum)) {
            return '-';
        } else {
            setSavedExtraAmount(getCostPerUnitNumber((extraSum * product?.period?.discount) / 100));
            return (
                <>
                    {sign}
                    {savedExtraAmount > 0
                        ? formatNumberWithSeparator(extraSum - savedExtraAmount, ',', 2)
                        : extraSum ?? '-'}
                </>
            );
        }
    }, [includeExtra, product?.tariff?.cost, product?.period?.interval, product?.period?.discount, product?.extra_package_rate, currency?.rate, savedExtraAmount, billingUnits]);

    useEffect(() => {
        let extraCostPer = Number(getExtraCostPer()).toFixed(2);

        if (!extraCostPer || isNaN(extraCostPer) || billingUnits === 0) {
            setExtraSumPerSite('-');
        } else {
            setExtraSumPerSite(getCostPerUnitNumber(extraCostPer / billingUnits))
            setSavedExtraPerSite((getCostPerUnitNumber(extraSumPerSite * product?.period?.discount)) / 100)
        }
    }, [billingUnits, product?.period?.discount, extraSumPerSite, product?.tariff?.cost, includeExtra, product?.period?.interval, currency?.rate, product?.extra_package_rate])

    useEffect(() => {
        if (isPeriodDifferentByProducts(mainProduct, product)) {
            setBillingUnits(null);
        } else {
            setBillingUnits(getBillingUnitsCount(product, cart));
        }
    }, [product?.product_id, product?.tariff, product?.period, cart?.domains]);

    useEffect(() => {
        if (isPeriodDifferentByProducts(mainProduct, product)) {
            setCostPerSite('-');
            setTariffCost('-');
            return;
        }
        const cost = getCostPerUnitNumber(
            product?.tariff?.cost * currency?.rate * product?.period?.interval
        );

        let costCalc = ((product?.tariff?.cost * currency?.rate) / billingUnits) * (1 - product?.period?.discount / 100);
        let costUnit = cost;

        if (product?.product_id === 5) {
            costCalc = (product?.tariff?.cost * currency?.rate) * (1 - product?.period?.discount / 100);
            costUnit =  costUnit * billingUnits;
        }

        if (billingUnits) {
            setCostPerSite(
                <>
                    {sign}
                    {getCostPerUnitNumber(costCalc)}
                </>
            );
        } else {
            setCostPerSite('-');
        }
        if (cost > MINIMUM_ALLOWED_PRODUCT_PRICE) {
            setTariffCost(
                <>
                    {sign}
                    {savedAmount > 0 ? formatNumberWithSeparator(cost - savedAmount, ',', 2) : costUnit}
                </>
            );
        } else {
            setTariffCost('-');
        }
        if (costUnit > 0) {
            setSavedAmount(getCostPerUnitNumber((costUnit * product?.period?.discount) / 100));
        }
    }, [product?.tariff, product?.period, currency, cart?.domains, billingUnits, product?.tariff?.cost, product?.period?.interval, currency?.rate, savedAmount]);

    const handleExtraVisible = () => {
        setExtraVisible((prev) => !prev);
    };

    const handleDescriptionVisible = () => {
        setDescriptionVisible((prev) => !prev);
    };

    const handleIncludeExtra = () => {
        if (product?.includeExtra) {
            mainProduct?.product_id !== 5
                ? setModalOpened(true)
                : includeExtra({ value: !product?.includeExtra, product_id: product_id });
        } else {
            includeExtra({ value: !product?.includeExtra, product_id: product_id });
        }
    };

    return (
        <>
            <div
                className={cn('section__row section__row--main', {
                    'section__row--closed': !descriptionVisible,
                })}
            >
                <div className={'section__cell section__cell--name'}>
                    <div>
                        <summary className={'title-3 section__title--spaced'}>
                            {pageLoading ? (
                                <Skeleton width={228} inline={true} />
                            ) : (
                                <>
                                    {product?.product_name}
                                    <CirclePlus
                                        onClick={handleDescriptionVisible}
                                        className={cn(
                                            'icon icon-rotated size-20 ml-10 cursor-pointer',
                                            { 'icon--rotated-180': descriptionVisible }
                                        )}
                                    />
                                </>
                            )}
                        </summary>
                        <div
                            className={cn('section--mobile-space-compensate', {
                                hidden: !descriptionVisible,
                            })}
                        >
                            <span className="accordion__summary"></span>
                            {pageLoading ? (
                                <div className={'list list--2-col'}>
                                    <Skeleton width={180} count={8} className={'ml-28 mt-13'} />
                                </div>
                            ) : (
                                <ul className={'list list--2-col'}>
                                    {product?.product_description_items?.map((item, i) => (
                                        <li key={'product-description' + i}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
                <div className={'section__cell section__cell--sites tablet-pt-7 tablet-pb-7'}>
                    <span className={'text-3 bold hide-desktop'}>Units</span>
                    <span className={'text-3'}>
                        {pageLoading ? <Skeleton width={30} inline={true} /> : billingUnits ? formatNumberWithSeparator(billingUnits) : '-'}
                    </span>
                </div>
                <div className={'section__cell section__cell--per-site tablet-pt-7 tablet-pb-7'}>
                    <span className={'text-3 bold hide-desktop'}>Cost per unit/year</span>
                    <span className={'text-3'}>
                        {pageLoading ? <Skeleton width={80} inline={true} /> : costPerSite}
                    </span>
                </div>
                <div className={'section__cell section__cell--sum tablet-pt-7'}>
                    <span className={'text-3 bold hide-desktop'}>Sum</span>
                    <span className={'text-3 tablet-ml-auto'}>
                        {pageLoading ? (
                            <Skeleton width={80} inline={true} />
                        ) : product?.tariff ? (
                            <>
                                {tariffCost}
                                {savedAmount > 0 ? (
                                    <i className={'color--green'}>
                                        {' '}
                                        (save&nbsp;{sign}
                                        {formatNumberWithSeparator(savedAmount, ',', 2)})
                                    </i>
                                ) : null}
                            </>
                        ) : (
                            '-'
                        )}
                    </span>
                </div>
            </div>
            {product?.extra_package_name && (
                <>
                    <div
                        className={cn('section__row section__row--extra', {
                            'is-active': mainProduct?.includeExtra,
                        })}
                    >
                        <div className={'section__cell section__cell--name'}>
                            <div className={'flex flex--column'}>
                                <div className={'flex'}>
                                    <div className="checkbox checkbox--extra">
                                        <input
                                            type="checkbox"
                                            id={`extra-${product_id}`}
                                            className="checkbox__input"
                                            /* Extra flag is switching "globally" (for all products) */
                                            checked={mainProduct?.includeExtra || false}
                                            onChange={handleIncludeExtra}
                                            disabled={
                                                (checkboxExtraPg && [1,4].includes(mainProduct?.product_id))
                                                ||
                                                (mainProduct?.product_id !== product?.product_id && !product?.isIncluded)
                                            }
                                        />

                                        <label
                                            htmlFor={`extra-${product_id}`}
                                            className={'checkbox__label'}
                                        >
                                            <p className={'text-3 extra-package'}>
                                                <span className={'extra-package--name'}>
                                                    {product?.extra_package_name}
                                                </span>
                                            </p>
                                        </label>
                                    </div>
                                    <CirclePlus
                                        onClick={handleExtraVisible}
                                        className={cn(
                                            'icon icon--rotated size-20 ml-10 cursor-pointer',
                                            {
                                                'icon--rotated-180': extraVisible,
                                            }
                                        )}
                                    />
                                </div>
                                {product_id !== mainProduct?.product_id && (
                                    <p className={'extra-package--note'}>
                                        {getExtraPackageNote(mainProduct)}
                                    </p>
                                )}
                                {product_id == mainProduct?.product_id && (
                                    <p className={'extra-package--note mt-4'}>
                                        {getExtraPackageNoteMainProduct(mainProduct)}
                                    </p>
                                )}
                            </div>

                            <div className={cn({ hidden: !extraVisible })}>
                                <div className="accordion__summary"></div>
                                <ul className={'list list--2-col'}>
                                    {product?.extra_package_description_items?.map((item, i) => (
                                        <li key={'extra-package-description' + i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div
                            className={'section__cell section__cell--sites tablet-pt-7 tablet-pb-7'}
                        >
                            <span className={'text-3 bold hide-desktop'}>Sites</span>
                            <span className={'text-3'}>
                                {pageLoading ? <Skeleton width={30} inline={true} /> : (mainProduct?.includeExtra && billingUnits > 0 ? billingUnits : '-')}
                            </span>
                        </div>
                        <div
                            className={
                                'section__cell section__cell--per-site tablet-pt-7 tablet-pb-7'
                            }
                        >
                            <span className={'text-3 bold hide-desktop'}>Cost per site</span>
                            { mainProduct?.includeExtra && !isNaN(extraSumPerSite)
                                ? (<span className={'text-3'}>
                                        <span className={'text-3 tablet-ml-auto'}>
                                            {sign}
                                            {savedExtraPerSite > 0 ? getCostPerUnitNumber(extraSumPerSite - savedExtraPerSite) : (extraSumPerSite || '-')}
                                        </span>
                                    </span>)
                                : '-'}
                        </div>
                        <div className={'section__cell section__cell--sum tablet-pt-7'}>
                            {mainProduct?.includeExtra && (
                                <>
                                    <span className={'text-3 bold hide-desktop'}>Sum</span>
                                    <span className={'text-3 tablet-ml-auto'}>{sum}</span>
                                    {savedExtraAmount > 0 ? (
                                        <i className={'color--green'}>
                                            &nbsp;(save&nbsp;{sign}
                                            {savedExtraAmount})
                                        </i>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                    {mainProduct?.includeExtra && (
                        <ReactModal
                            isOpen={modalOpened}
                            contentLabel="onRequestClose Example"
                            onRequestClose={() => setModalOpened(false)}
                            shouldCloseOnOverlayClick={false}
                            portalClassName={'modal__container'}
                            overlayClassName={'modal__overlay'}
                            className={'modal__content'}
                            bodyOpenClassName={'body--modal-opened'}
                            htmlOpenClassName={'html--modal-opened'}
                            ariaHideApp={false}
                            closeTimeoutMS={500}
                        >
                            <ExtraPackageDetails
                                setModalOpened={setModalOpened}
                                setExtraChecked={includeExtra}
                                product_id={product_id}
                                list={product?.extra_package_description_items}
                            />
                        </ReactModal>
                    )}
                </>
            )}
        </>
    );
}

const mapStateToProps = (state) => ({
    mainProductId: productCurrentProductIdSelector(state),
    mainProduct: cartMainProduct(state),
    cart: cartCartSelector(state),
    currency: cartCurrencySelector(state),
    pageLoading: pageLoading(state),
});

export default connect(mapStateToProps, null)(TableBody);
